import { useState, useCallback, useEffect } from 'react';
import cx from 'classnames';
import * as THREE from 'three';
import type { GlbSceneApi } from '../../scene/glb/createGlbScene';
import { GLB_BG_THEME_HEX, type GlbBgTheme } from '../../scene/glb/glbSceneConfig';
import s from './SceneBackgroundSwitcher.module.css';

type BgColor = GlbBgTheme;

const STORAGE_KEY = 'pilgrim.insideBgColor';

const readStorageMap = (): Record<string, BgColor> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, BgColor>;
    }
  } catch { /* private mode or legacy string format */ }
  return {};
};

const readStoredBg = (sceneId: string, defaultBg: BgColor): BgColor => {
  const value = readStorageMap()[sceneId];
  return value === 'white' || value === 'black' ? value : defaultBg;
};

const writeStoredBg = (sceneId: string, color: BgColor): void => {
  try {
    const map = readStorageMap();
    map[sceneId] = color;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch { /* private mode */ }
};

const applyBg = (api: GlbSceneApi, color: BgColor) => {
  const hex = GLB_BG_THEME_HEX[color];
  api.scene.background = new THREE.Color(hex);
  api.renderer.setClearColor(hex);
};

interface SceneBackgroundSwitcherProps {
  api: GlbSceneApi;
  sceneId: string;
  defaultBg?: BgColor;
  fullscreen?: boolean;
}

export const SceneBackgroundSwitcher = ({
  api,
  sceneId,
  defaultBg = 'white',
  fullscreen,
}: SceneBackgroundSwitcherProps) => {
  const [active, setActive] = useState<BgColor>(() => readStoredBg(sceneId, defaultBg));

  useEffect(() => {
    applyBg(api, active);
  }, [api, active]);

  const handleClick = useCallback(
    (color: BgColor) => {
      setActive(color);
      writeStoredBg(sceneId, color);
    },
    [sceneId],
  );

  return (
    <div className={cx(s.root, fullscreen && s.rootFullscreen)}>
      <button
        className={cx(s.swatch, active === 'white' && s.swatchActive)}
        aria-label="White background"
        onClick={() => handleClick('white')}
      >
        <span className={s.swatchWhite}/>
      </button>
      <button
        className={cx(s.swatch, active === 'black' && s.swatchActive)}
        aria-label="Black background"
        onClick={() => handleClick('black')}
      >
        <span className={s.swatchBlack}/>
      </button>
    </div>
  );
};
